/**
 * Constantes globales del sitio. Centralizadas aquí para que cambios de
 * email, dominio o branding se hagan en un solo lugar (en vez de en
 * cinco diccionarios de i18n distintos).
 */
/**
 * Resuelve la URL del sitio con fallback robusto.
 * - Acepta env vars vacías (común en build sin .env de producción).
 * - Garantiza un valor válido para `new URL()` en metadata y schemas.
 */
// 2026-09-14 FASE 2 P3 (canónica única en prod — Google Search Central 2026:
// canonical absoluta + hreflang recíproco + x-default; research 2026-09-14):
// en producción la URL canónica es siempre https://www.vortixy.net
// (trailingSlash:false intacto en next.config.ts) — un override de env en
// prod fragmentaría canónicas/sitemap/robots/OG. Fuera de prod se permite el
// override (previews, E2E) con validación de formato.
const CANONICAL_PROD_URL = "https://www.vortixy.net";

function resolveSiteUrl(): string {
  if (process.env.NODE_ENV === "production") return CANONICAL_PROD_URL;
  const raw = process.env.NEXT_PUBLIC_SITE_URL?.trim().replace(/\/$/, "");
  if (raw) {
    try {
      const parsed = new URL(raw);
      if (parsed.protocol === "http:" || parsed.protocol === "https:")
        return raw;
    } catch {
      // Formato inválido → canónica por defecto (nunca romper metadata).
    }
  }
  return CANONICAL_PROD_URL;
}

function resolveContactEmail(): string {
  const fromPublicContact = process.env.NEXT_PUBLIC_CONTACT_EMAIL?.trim();
  if (fromPublicContact) return fromPublicContact;
  return "vortixyoficial@gmail.com";
}

export const SITE = {
  name: "Vortixy",
  tagline: "A calmer AI workspace for study and work",
  // 2026-09-14 SEO reorient (research 2026-09-14): product SSOT — Vortixy is a
  // single-product study chat at /chat (study, writing, research, planning,
  // everyday work). /humanizer, /detector, /paraphraser are legacy text guides
  // kept indexable so links don't break — supporting surfaces, not the product.
  // Public copy, metadata, sitemap priorities and llms.txt pillars derive from
  // this positioning (see src/app/sitemap.ts, src/app/llms.txt/route.ts).
  /** URL canonical en producción: siempre https://www.vortixy.net (pin en prod;
      fuera de prod, override vía NEXT_PUBLIC_SITE_URL). */
  url: resolveSiteUrl(),
  /** Email público de contacto / soporte. Sobreescribible vía NEXT_PUBLIC_CONTACT_EMAIL. */
  contactEmail: resolveContactEmail(),
  /** Año de fundación (para copyright "© 2026-AÑO_ACTUAL Vortixy"). */
  foundingYear: 2026,
  /** Fecha de la última actualización legal — bumpear cuando cambien los términos.
   *  2026-09-28 FASE 2 RUN6 I18NSEO-02 (research 2026 Google Search Central
   *  build-sitemap: lastmod debe reflejar el "last significant update"; Illyes
   *  2026-07-16 Bluesky: fechas de lastmod incorrectas hacen que Google deje de
   *  confiar en el campo — "sin fechas falsas", AGENTS.md). Verificado con
   *  `git log`: el último commit que cambia contenido legal real es
   *  d3d735aa (2026-09-28, "docs(privacy): clarify sensitive-data handling")
   *  → añadió privacySensitiveTitle/Body a las 14 + la sección de datos
   *  sensibles a /privacy y a los mapas AI. Antes decía 2026-07-18, que ya
   *  no correspondía a /privacy y se propagaba a /terms /aup /dmca,
   *  al JSON-LD legal y a llms.txt/llms-full.txt. */
  legalLastUpdated: "2026-09-28",
  /** Fecha de la última actualización de contenido — actualizar en cambios significativos de páginas. */
  // 2026-10-09 RUN17: contenido visible cambió 2026-10-08/09 — antes 2026-10-05 (RUN15 SEO S7: ProductFilm click-to-play + starfield Study + hub académico). Un <lastmod> falso en 19 rutas ×14 locales hace que Google deje de confiar en el campo (Google freshness 2026).
  // 2026-10-13 E5 (bucket status/contenido): hoy solo cambian headers/CSP,
  // caché, a11y y gates (0 píxeles visibles) → NO se bumpea. Bumpear sin
  // cambio real quema la confianza en lastmod (Illyes 2026 — ver nota RUN17).
  contentLastUpdated: "2026-10-09",
  /** Jurisdicción para cláusulas legales (ley aplicable + arbitraje). — 2026-08-23 República de Colombia */
  jurisdiction: "República de Colombia",
  /** Razón social a efectos legales (cambiar cuando se constituya la sociedad). */
  legalEntity: "Vortixy",
  /** Fundador (SSOT para llms.txt/llms-full.txt + JSON-LD Organization — pinta
      desde SITE, server sin t(), 0 keys i18n nuevas). */
  founder: "Johan Esteban Bustos Caicedo",
  /** Ubicación del fundador (par del founder Person en ORGANIZATION_SCHEMA). */
  founderLocation: "Colombia",
  /** Versión de los iconos raster (favicon/PWA/OG). Bumpear cuando cambie el contenido — los headers son immutable 1 año. SSOT para `?v=` en URLs de iconos. */
  iconVersion: "3",
} as const;

export const SITE_URL = SITE.url;
export const CONTACT_EMAIL = SITE.contactEmail;

/**
 * Cadena de copyright lista para mostrar en footer / metadata.
 * Devuelve "© 2026 Vortixy" o "© 2026–2028 Vortixy" según el año actual.
 */
export function copyrightLine(
  name: string = SITE.name,
  currentYear: number = new Date().getFullYear(),
  allRightsReserved?: string,
): string {
  const safeYear = Math.max(SITE.foundingYear, currentYear);
  const range =
    safeYear > SITE.foundingYear
      ? `${SITE.foundingYear}\u2013${safeYear}`
      : `${SITE.foundingYear}`;
  const rights = allRightsReserved ?? "All rights reserved.";
  return `\u00A9 ${range} ${name}. ${rights}`;
}
